import { useToast } from "@chakra-ui/react";
import {
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { SR } from "modules/api/websockets";
import { useMemberAuthStore } from "../auth";
import { readBody } from "./ipRestriction";
export { httpStatusOf } from "./httpStatus";

export type MemberTokenResponse = {
  memberId: string;
  orgId: string;
  accessToken: string;
};

let sessionGeneration = 0;
// The refresh running for the current session, shared by every caller that
// asks while it is in flight.
let inFlight: {
  generation: number;
  promise: Promise<{ accessToken: string } | null>;
} | null = null;

// A refresh still in flight when the member signs out would otherwise land
// afterwards and sign them straight back in with a token logout is revoking.
export const endSession = () => {
  sessionGeneration += 1;
};

/**
 * Gets a fresh access token and signs the member in with it.
 *
 * A caller that asks while a refresh for this session is already running gets
 * that refresh's result rather than null: several requests that hit 401 at
 * once (or a page that mounts during the startup refresh) all wait for the one
 * token instead of reading "no token" as an expired session. Resolves null only
 * when the session ended while the refresh was in flight.
 */
export const refresh = (): Promise<{ accessToken: string } | null> => {
  const generation = sessionGeneration;
  if (inFlight && inFlight.generation === generation) return inFlight.promise;

  const promise: Promise<{ accessToken: string } | null> =
    refreshToken<MemberTokenResponse>()
      .then((r: MemberTokenResponse) => {
        if (generation !== sessionGeneration) return null;
        localStorage.setItem("memSignedIn", "true");
        useMemberAuthStore.setState({
          isSignedin: true as const,
          accessToken: r.accessToken,
          id: r.memberId,
          orgId: r.orgId,
        });
        return { accessToken: r.accessToken };
      })
      .catch((e) => {
        if (generation === sessionGeneration) {
          useMemberAuthStore.setState({ isSignedin: false as const });
        }
        return Promise.reject(e);
      })
      .finally(() => {
        if (inFlight?.promise === promise) inFlight = null;
      });

  inFlight = { generation, promise };
  return promise;
};

export const API_BASE = import.meta.env.PROD
  ? import.meta.env.VITE_API_URL
  : import.meta.env.VITE_API_URL || "http://localhost:4000/";

export const refreshToken = <T>(): Promise<T> =>
  fetch(API_BASE + "members/auth/refresh", {
    method: "POST",
    credentials: "include",
    headers: {
      "x-org-subdomain": getSubdomain(),
    },
  }).then((r) => (r.ok ? r.json() : Promise.reject(r)));

export const logout = <T>(): Promise<T> =>
  fetch(API_BASE + "members/auth/logout", {
    method: "POST",
    credentials: "include",
  }).then((r) => (r.ok ? r.json() : Promise.reject(r)));

export const useMemberGetQuery = <T>(
  key: QueryKey,
  path: string,
  options: any = {},
) => {
  const accessToken = useMemberAuthStore((state) => state.accessToken);
  const resp = useQuery(
    [...key, accessToken],
    async () => {
      return membersApi.get<T>(path, accessToken as string);
    },
    {
      ...options,
      enabled:
        "enabled" in options
          ? Boolean(accessToken && options.enabled)
          : Boolean(accessToken),
    },
  );
  return { ...resp, data: resp.data?.success ? resp.data.data : undefined };
};

export type InfiniteQueryParams<T> = {
  filters: T;
  pagination: Pagination;
};

export type Pagination = {
  limit: number;
  after: number;
  sort_direction?: "desc" | "asc";
  sort_by?: string;
};

export type Sorting = Pick<Pagination, "sort_direction" | "sort_by">;

export const useMemberPostQuery = <T>(
  key: QueryKey,
  path: string,
  body: any,
) => {
  const accessToken = useMemberAuthStore((state) => state.accessToken);
  const resp = useQuery(
    key,
    async () => membersApi.post<T>(path, body, accessToken as string),
    { enabled: Boolean(accessToken) },
  );
  return { ...resp, data: resp.data?.success ? resp.data.data : undefined };
};

type MutateOpts = {
  includeAuth?: boolean;
  authToken?: string;
  mutationKey?: QueryKey;
  /** A refusal the caller handles itself, so no error toast is shown for it. */
  quietWhen?: (error: unknown) => boolean;
};
export const useMemberMutate = <T>(
  method: "POST" | "PUT",
  path: string,
  invalidationKeys: QueryKey[] = [],
  opts: MutateOpts = {},
) => {
  const defaultedOpts: MutateOpts = { includeAuth: true, ...opts };
  const accessToken = useMemberAuthStore((state) => state.accessToken);
  const queryClient = useQueryClient();
  const toast = useToast();
  const { mutateAsync, isSuccess, isLoading } = useMutation(
    async (body: any) => {
      const token = defaultedOpts.includeAuth
        ? ((opts.authToken || accessToken) as string)
        : "";
      return method === "PUT"
        ? membersApi.put<T>(path, body, token)
        : membersApi.post<T>(path, body, token);
    },
    {
      mutationKey: opts.mutationKey,
      onSuccess: () => {
        invalidationKeys.forEach((k) => {
          queryClient.invalidateQueries({ queryKey: k });
        });
      },
      onError: (res: any) => {
        if (opts.quietWhen?.(res)) return;
        // Never surface "Error: undefined" — a missing/blank server error field
        // (e.g. a non-standard error body) falls back to a generic message.
        const error = res?.error;
        toast({
          status: "error",
          title:
            typeof error === "string" && error.trim()
              ? "Error: " + error
              : "Something went wrong",
          description: res?.description?.[0],
          variant: "subtle",
        });
      },
    },
  );
  return { mutate: mutateAsync, isSuccess, isLoading };
};

export const useMemberInfinitePostQuery = <Filters, T>(
  key: QueryKey,
  path: string,
  filters: Filters,
  limit: number,
  sorting: Sorting,
  options: UseInfiniteQueryOptions = {},
) => {
  const accessToken = useMemberAuthStore((state) => state.accessToken);
  return useInfiniteQuery(
    [...key, sorting, accessToken],
    async ({ pageParam }) => {
      const token = accessToken as string;
      const res = await membersApi.post<T[]>(
        path,
        { filters, pagination: { limit, after: pageParam || 0, ...sorting } },
        token,
      );
      return {
        rows: res.success ? res.data : [],
        after: pageParam || 0,
      };
    },
    {
      enabled: Boolean(accessToken),
      getNextPageParam: (lastPage: any) =>
        lastPage.rows.length === limit ? lastPage.after + limit : undefined,
      suspense: false,
      ...(options as any),
    },
  );
};
export const getSubdomain = () =>
  import.meta.env.PROD
    ? window.location.hostname.split(".")[0]
    : import.meta.env.VITE_DEV_SUBDOMAIN || "ploy";

// A refusal rejects with the server's body as before, plus the HTTP status,
// which the body alone doesn't carry.
export type MembersApiFailure<T = unknown> = SR<T> & { httpStatus: number };

const settle = async <T>(res: Response): Promise<SR<T>> => {
  const body = await readBody<SR<T>>(res);
  if (body?.success) return body;
  return Promise.reject(
    body && typeof body === "object"
      ? ({ ...body, httpStatus: res.status } as MembersApiFailure<T>)
      : body,
  );
};

export const membersApi = {
  post: async <T>(
    path: string,
    body: any,
    token: string,
    headers: any = {},
  ): Promise<SR<T>> => {
    return fetch(API_BASE + cleanPath(path), {
      method: "POST",
      headers: {
        ...headers,
        "content-type": "application/json",
        authorization: "Bearer " + token,
        "x-org-subdomain": getSubdomain(),
      },
      body: JSON.stringify(body),
    }).then((res) => settle<T>(res));
  },
  put: async <T>(
    path: string,
    body: any,
    token: string,
    headers: any = {},
  ): Promise<SR<T>> => {
    return fetch(API_BASE + cleanPath(path), {
      method: "PUT",
      headers: {
        ...headers,
        "content-type": "application/json",
        authorization: "Bearer " + token,
        "x-org-subdomain": getSubdomain(),
      },
      body: JSON.stringify(body),
    }).then((res) => settle<T>(res));
  },
  delete: async <T>(
    path: string,
    token: string,
    headers: any = {},
  ): Promise<SR<T>> => {
    return fetch(API_BASE + cleanPath(path), {
      method: "DELETE",
      headers: {
        ...headers,
        "content-type": "application/json",
        authorization: "Bearer " + token,
        "x-org-subdomain": getSubdomain(),
      },
    }).then((res) => settle<T>(res));
  },
  get: async <T>(
    path: string,
    token: string,
    headers: any = {},
    refreshed = false,
  ): Promise<SR<T>> => {
    return fetch(API_BASE + cleanPath(path), {
      method: "GET",
      headers: {
        ...headers,
        "content-type": "application/json",
        authorization: "Bearer " + token,
        "x-org-subdomain": getSubdomain(),
      },
    })
      .then((res) => readBody<SR<T>>(res))
      .then((res: SR<T>) => {
        if (res?.success || (res.error && res.error === "not_found")) {
          return res;
        } else if (res.error === "invalid token" && !refreshed) {
          return refresh().then(
            (r) => r && membersApi.get<T>(path, r?.accessToken, headers, true),
          ) as any;
        } else {
          return Promise.reject(res.error);
        }
      });
  },
};

const cleanPath = (path: string) =>
  path.startsWith("/") ? path.slice(1) : path;

const buildUrl = (base: string, path: string) =>
  path.startsWith("/") ? base + path.slice(1) : base + path;
